import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { getTourPreview } from "../../services/admin-tours";
import { getMyTours, recordTourProgress } from "../../services/tours";
import type { RecordTourProgressRequest, TourOutcome } from "../../types/tours";
import { WEB_TOUR_ANCHORS } from "./tourAnchors";
import {
  TourContext,
  type PageRegistration,
  type ReplayRequest,
  type TourContextValue,
} from "./tourContext";
import {
  dueRun,
  findProgress,
  permissionsForTours,
  renderableSteps,
  replayRun,
  resolveTourKeys,
  type RenderContext,
  type TourKeyOverrides,
  type TourRun,
} from "./tourEngine";
import {
  backTarget,
  closeAnchorsOpenedByRun,
  closePopupsLeftBehind,
  scheduleFrame,
} from "./tourDom";
import type { TourState } from "./tourReducer";
import { TourOverlay } from "./TourOverlay";
import { TourPickMode } from "./TourPickMode";
import { TourPreviewNotice } from "./TourPreviewNotice";
import { createTourProgressGuard, type TourProgressGuard } from "./tourProgressGuard";
import { initialTourState, tourReducer } from "./tourReducer";

const sendProgress = (
  guard: TourProgressGuard,
  tourKey: string,
  body: RecordTourProgressRequest
) =>
  recordTourProgress(tourKey, body)
    .then(() => guard.removePending(tourKey, body.seenAt))
    .catch((error) => {
      // Kept as a pending write and retried on the next load for this user.
      console.warn(`Could not record progress for tour ${tourKey}; will retry:`, error);
    });

/**
 * Whether every step of `run` is rendered as a centered card: a data tour whose page has lost the
 * rows it points at. Asked both while rendering and by Back, so the two cannot disagree about what
 * the coach is looking at.
 */
const anchorSuppressed = (state: TourState, run: TourRun): boolean =>
  run.tour.kind === "Data" && state.pages[run.tour.feature]?.hasData === false;

interface TourProviderProps {
  children: ReactNode;
}

/**
 * Loads guided tours for the signed-in user and runs at most one at a time.
 *
 * Mounted inside `AuthProvider` and the router. Tours are optional: any failure to load them
 * (an API without tours, a network error, a paused organization) disables them silently.
 */
export const TourProvider = ({ children }: TourProviderProps) => {
  const { user, effectiveOrganizationId, isSystemAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(tourReducer, initialTourState);

  const userId = user?.profile?.id ?? null;
  const scope = userId && effectiveOrganizationId ? `${userId}|${effectiveOrganizationId}` : null;
  const role = user?.profile?.role;
  const permissions = user?.profile?.permissions;

  const renderContext = useMemo<RenderContext>(
    () => ({ knownAnchors: WEB_TOUR_ANCHORS, permissions: permissionsForTours(role, permissions) }),
    [role, permissions]
  );

  const guard = useMemo(
    () =>
      userId && effectiveOrganizationId
        ? createTourProgressGuard(userId, effectiveOrganizationId)
        : null,
    [userId, effectiveOrganizationId]
  );

  /**
   * Cancels an end that is waiting for a frame (see `anchorUsed`). Unmounting is leaving the page
   * mid-tour, which closes the run and records nothing, so the pending end goes with it.
   */
  const cancelPendingEnd = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      cancelPendingEnd.current?.();
      cancelPendingEnd.current = null;
    },
    []
  );

  // Latest state for event handlers, so the callbacks below can stay referentially stable.
  const stateRef = useRef(state);
  const renderContextRef = useRef(renderContext);
  useLayoutEffect(() => {
    stateRef.current = state;
    renderContextRef.current = renderContext;
  });

  /*
   * Closes what the active run opened, while the overlay is still mounted and the steps and their
   * record are still to hand. `closeAnchorsOpenedByRun` presses only what this run itself opened
   * and what is still open.
   *
   * Every way a run leaves the screen has to come through here, not only `endRun`: a run that goes
   * without it leaves the popup it asked the coach to open sitting behind whatever runs next,
   * inerted and with nothing to close it.
   *
   * `extraSatisfiedKey` is a press that has just landed and is not in the run's record yet. The
   * record only refreshes on the re-render after the dispatch that adds it, so a press made on the
   * run's *final* step — the press that ends the run — would otherwise be invisible to a tidy-up
   * asked for in the same handler.
   */
  const closeWhatTheRunOpened = useCallback((extraSatisfiedKey: string | null = null) => {
    const active = stateRef.current.active;
    if (!active) return;
    const satisfied =
      extraSatisfiedKey === null || active.satisfiedStepKeys.includes(extraSatisfiedKey)
        ? active.satisfiedStepKeys
        : [...active.satisfiedStepKeys, extraSatisfiedKey];
    closeAnchorsOpenedByRun(active.run.steps, satisfied);
  }, []);

  // Load once per user and effective organization. Switching either, or logging out, drops the
  // running tour without recording it.
  useEffect(() => {
    if (!guard || !scope) {
      dispatch({ type: "reset" });
      return;
    }
    let cancelled = false;
    dispatch({ type: "loadStart", scope });

    getMyTours()
      .then((response) => {
        if (cancelled) return;
        // After the load rather than before it: only the tours say when each was last re-shown, and
        // a retried write from before a re-show would mark the tour seen again on the server.
        guard.pendingWrites(response.tours).forEach((write) => {
          void sendProgress(guard, write.tourKey, write.body);
        });
        dispatch({
          type: "loadSucceeded",
          scope,
          tours: response.tours,
          progress: guard.mergeInto(response.progress, response.tours),
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn("Guided tours are unavailable:", error);
        dispatch({ type: "loadFailed", scope });
      });

    return () => {
      cancelled = true;
    };
  }, [guard, scope]);

  // Another tab finished or skipped a tour: close it here too, and never show it again.
  useEffect(() => {
    if (!guard) return;
    return guard.subscribe((tourKey) => {
      const local = guard
        .mergeInto([], stateRef.current.tours)
        .find((entry) => entry.tourKey === tourKey);
      dispatch({ type: "doneElsewhere", tourKey, seenStepKeys: local?.seenStepKeys ?? [] });
    });
  }, [guard]);

  /**
   * A replay chosen from the Guided tours list, waiting for its page's `useTour` to run it.
   *
   * Its lifetime is the visit to `request.path`. It is dropped when the hook consumes it, when the
   * pathname changes to anything other than that path, and when the user or organization changes.
   * So a request whose page never became ready cannot fire later on some unrelated visit. The
   * navigation that carries the user to the page does not drop it: that change lands on `path`.
   */
  const [pendingReplay, setPendingReplay] = useState<ReplayRequest | null>(null);

  useEffect(() => {
    setPendingReplay(null);
  }, [scope]);

  // Leaving the page mid-tour closes it and records nothing. Compared on pathname, not the
  // location key: a page replacing its own search params is not leaving.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (pathnameRef.current === location.pathname) return;
    const pathname = location.pathname;
    pathnameRef.current = pathname;
    dispatch({ type: "close" });
    setPendingReplay((current) => (current && current.path !== pathname ? null : current));
  }, [location.pathname]);

  /**
   * The page a preview was run on. Auto-start stays held there until the admin leaves it: dropping
   * the parameter is a navigation, and without the hold the published version, if due, would start
   * straight after the draft and be recorded as seen.
   */
  const [previewedPath, setPreviewedPath] = useState<string | null>(null);
  const previewVisit = previewedPath !== null && previewedPath === location.pathname;

  /*
   * The location key settles a frame after it changes, so auto-start never measures the old page.
   *
   * Held in a ref and handed out through `whenRouteSettled`, never as state on the context value:
   * search params are navigation too, so a filter or a page change would otherwise re-render every
   * consumer twice. A layout effect, so it is marked unsettled before any passive effect in the
   * same commit (`useTour`'s auto-start) can ask.
   */
  const routeRef = useRef({ key: location.key, settled: true });
  const settleListeners = useRef(new Set<() => void>());
  useLayoutEffect(() => {
    // Keyed on "settled" as well as the key, so StrictMode's re-run after a cancelled frame
    // schedules the frame again instead of leaving the route unsettled for good.
    if (routeRef.current.settled && routeRef.current.key === location.key) return;
    const key = location.key;
    routeRef.current = { key, settled: false };
    return scheduleFrame(() => {
      routeRef.current = { key, settled: true };
      const listeners = Array.from(settleListeners.current);
      settleListeners.current.clear();
      listeners.forEach((listener) => listener());
    });
  }, [location.key]);

  const whenRouteSettled = useCallback((callback: () => void) => {
    if (routeRef.current.settled) {
      callback();
      return () => {};
    }
    const listeners = settleListeners.current;
    listeners.add(callback);
    return () => {
      listeners.delete(callback);
    };
  }, []);

  /**
   * `?tourPreview=<key>`: a system admin looking at an unpublished draft.
   *
   * It runs as a replay, so it ignores seen state and records nothing, locally or on the server,
   * and the bubble carries a Preview marker. Anyone else gets the parameter ignored outright - the
   * endpoint is admin-only, and asking for it as a coach would only produce a console error.
   */
  const adminParams = useMemo(
    () => (isSystemAdmin ? new URLSearchParams(location.search) : null),
    [isSystemAdmin, location.search]
  );

  const previewKey = adminParams?.get("tourPreview") ?? null;

  /**
   * `?tourPick=<key>`: a system admin choosing the element a new step should point at.
   *
   * While it is on, no tour runs. The overlay makes every other child of `body` inert, so a tour
   * that auto-started here would leave nothing on the page to pick - and the pages worth picking on
   * are exactly the ones with a tour due. Anyone else gets the parameter ignored outright.
   */
  const pickKey = adminParams?.get("tourPick") ?? null;
  const picking = pickKey !== null;

  useEffect(() => {
    if (picking) dispatch({ type: "close" });
  }, [picking]);

  /**
   * Which preview has been dealt with, as `${scope}|${key}`: put on screen, or reported as failed.
   *
   * State rather than a ref, because it decides whether auto-start is held. Until the draft is on
   * screen the provider reports "disabled", exactly as pick mode does; otherwise the page's due
   * published tour starts a frame after the route settles, while the preview is still loading, and
   * the admin watches the published tour instead and has it recorded as seen. Carrying the scope
   * means switching organization runs the preview again there.
   */
  const previewToken = previewKey && scope ? `${scope}|${previewKey}` : null;
  const [settledPreview, setSettledPreview] = useState<string | null>(null);
  const [previewFailure, setPreviewFailure] = useState<{ token: string; message: string } | null>(
    null
  );
  const previewPending = previewToken !== null && settledPreview !== previewToken;
  const previewError =
    previewFailure && previewFailure.token === previewToken ? previewFailure.message : "";

  // The parameter going away forgets the mark, so the same preview link opened again runs again.
  useEffect(() => {
    if (!previewKey) setSettledPreview(null);
  }, [previewKey]);

  const stripPreviewParam = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("tourPreview")) return;
    setPreviewedPath(location.pathname);
    params.delete("tourPreview");
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "" },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!previewKey || !previewToken || !previewPending) return;
    // The config for this scope, not a previous one still in state while the new one loads.
    if (state.status !== "ready" || state.scope !== scope) return;
    // Pick mode hides the overlay: a preview started under it would be marked shown unseen.
    if (picking) return;

    const controller = new AbortController();
    getTourPreview(previewKey, "Web", controller.signal)
      .then((previewTour) => {
        if (controller.signal.aborted) return;
        const run = previewTour ? replayRun(previewTour, renderContextRef.current) : null;
        if (run) {
          /*
           * Replaces anything already on screen. This is the one path that takes a run off the
           * screen without ending it, so it owes the page the same tidy-up `end` does — before the
           * replace, while the overlay is still mounted and the reducer still has the replaced
           * run's record of what it opened. Without it the panel that run opened is left inert
           * behind the preview, with nothing left that knows it was the tour that opened it.
           */
          closeWhatTheRunOpened();
          // It counts as previewed once it is seen active (below), not here: a start the reducer
          // refused must leave the preview free to retry.
          dispatch({ type: "start", run: { ...run, preview: true }, replace: true });
          return;
        }
        setPreviewFailure({
          token: previewToken,
          message:
            "That draft has no step this page can show yet. Save a draft with at least one enabled step.",
        });
        setSettledPreview(previewToken);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.warn(`Could not preview tour ${previewKey}:`, error);
        setPreviewFailure({
          token: previewToken,
          message: "That tour draft could not be loaded. Check the tour still exists.",
        });
        setSettledPreview(previewToken);
      });

    return () => controller.abort();
  }, [
    previewKey,
    previewToken,
    previewPending,
    picking,
    scope,
    state.status,
    state.scope,
    closeWhatTheRunOpened,
  ]);

  const activePreviewKey = state.active?.run.preview ? state.active.run.tour.key : null;
  useEffect(() => {
    if (previewToken && state.scope === scope && activePreviewKey === previewKey) {
      setSettledPreview(previewToken);
    }
  }, [activePreviewKey, previewKey, previewToken, scope, state.scope]);

  // The parameter goes when the preview ends, so a reload of the page the admin is standing on
  // does not start the draft over.
  const previewActive = activePreviewKey !== null;
  /** The scope a preview was last on screen in, or null when none has been since it ended. */
  const previewShownIn = useRef<string | null>(null);
  useEffect(() => {
    if (previewActive) {
      previewShownIn.current = state.scope;
      return;
    }
    const shownIn = previewShownIn.current;
    if (shownIn === null) return;
    previewShownIn.current = null;
    // A switch of user or organization closed it rather than the admin ending it: the parameter
    // stays, so the preview runs again in the new scope.
    if (shownIn === state.scope) stripPreviewParam();
  }, [previewActive, state.scope, stripPreviewParam]);

  const record = useCallback(
    (run: TourRun, outcome: TourOutcome, lastStepKey: string | null) => {
      if (run.replay || !guard) return;
      const body: RecordTourProgressRequest = {
        outcome,
        renderedStepKeys: run.steps.map((step) => step.key),
        lastStepKey,
        platform: "Web",
        seenAt: new Date().toISOString(),
      };
      // Stamped with the run's own time, so a later re-show can tell this mark is older than it.
      guard.markDone(run.tour.key, body.renderedStepKeys, body.seenAt);
      guard.addPending(run.tour.key, body);
      void sendProgress(guard, run.tour.key, body);
    },
    [guard]
  );

  /*
   * Finishing, skipping and chaining all come through here, and all three leave the page as the
   * run left it — which, for a tour that ends on a detour, means a popup the tour asked to have
   * opened and nothing to close it with. The page tour then chains the data tour, which inerts the
   * app root, and the popup sits there dimmed and unreachable for the whole of the next run.
   *
   * So the run closes what it opened before it goes, on its way out rather than after the state
   * change.
   */
  const endRun = useCallback(
    (outcome: TourOutcome, extraSatisfiedKey: string | null = null, closeOpened = true) => {
      const active = stateRef.current.active;
      if (!active) return;
      if (closeOpened) closeWhatTheRunOpened(extraSatisfiedKey);
      record(active.run, outcome, active.run.steps[active.stepIndex]?.key ?? null);
      dispatch({ type: "end", outcome, context: renderContext });
    },
    [closeWhatTheRunOpened, record, renderContext]
  );

  const finish = useCallback(() => endRun("Finished"), [endRun]);
  const skip = useCallback(() => endRun("Skipped"), [endRun]);

  const next = useCallback(() => {
    const active = stateRef.current.active;
    if (!active) return;
    if (active.stepIndex >= active.run.steps.length - 1) {
      finish();
    } else {
      // Leaving the last step inside a popup the run opened closes it, so the next step is not
      // pointing at a page hidden behind it.
      const { steps } = active.run;
      closePopupsLeftBehind(steps, active.satisfiedStepKeys, steps[active.stepIndex + 1] ?? null);
      dispatch({ type: "step", delta: 1 });
    }
  }, [finish]);

  /*
   * Back walks past every step that cannot be shown as authored right now, and lands on the last
   * one that can simply be read — without ever walking out of a dialog that is open.
   *
   * `backTarget` is the whole rule, and it asks the same questions the rest of the feature asks:
   * this run's record of presses already spent, whether the step's control is still on the page,
   * whether the overlay is rendering every step as a card anyway, and the same "is a dialog open"
   * test auto-start uses. Forward is untouched — every step is still shown once, in order.
   */
  const back = useCallback(() => {
    const current = stateRef.current;
    const active = current.active;
    if (!active) return;
    const target = backTarget(active.run.steps, active.stepIndex, {
      suppressAnchor: anchorSuppressed(current, active.run),
      satisfiedStepKeys: active.satisfiedStepKeys,
    });
    if (target === null) return;
    // Landing on the step that opened a popup: close what the run opened first, while its record of
    // presses still says what that was, so the coach is back on the page the step describes.
    if (target.rearm) closeWhatTheRunOpened();
    dispatch({ type: "goto", stepIndex: target.stepIndex, rearm: target.rearm });
  }, [closeWhatTheRunOpened]);

  /**
   * The coach used the control a `ClickToContinue` step named: record the click as spent, then
   * move on exactly as Next would. The record is what stops the step asking for the same press
   * again when Back and Next bring the coach past it a second time.
   *
   * The last step is the awkward one, and it is reachable: a run made of unseen steps only, or a
   * tour an admin ends on "open Columns", finishes on this press rather than on a Next. Two things
   * about that press are invisible to a tidy-up run in the same handler — the record of it, which
   * only lands on the next render, and the popup it opened, because the tour watches the click in
   * the capture phase and the control's own handler has not run yet. So the end waits a frame, by
   * which time both have landed, and is dropped if anything else ended the run first.
   */
  const anchorUsed = useCallback(() => {
    const active = stateRef.current.active;
    if (!active) return;
    const { run, stepIndex } = active;
    dispatch({ type: "anchorUsed" });
    if (stepIndex < run.steps.length - 1) {
      // The same check `next` makes. The record of this press has not landed yet, so it is passed
      // in; a popup this press opens is not open yet either, so only an earlier one can close.
      const currentKey = run.steps[stepIndex]?.key;
      closePopupsLeftBehind(
        run.steps,
        currentKey ? [...active.satisfiedStepKeys, currentKey] : active.satisfiedStepKeys,
        run.steps[stepIndex + 1] ?? null
      );
      dispatch({ type: "step", delta: 1 });
      return;
    }
    const stepKey = run.steps[stepIndex]?.key ?? null;
    // The record is written here, in the same task as the press, because the press may navigate:
    // the control's own handler runs next, and a route change closes the run before any frame of
    // ours arrives. Deferring the write would lose it, and the coach would be shown the tour again.
    const steps = run.steps;
    const satisfied = stepKey === null ? [...active.satisfiedStepKeys] : [...active.satisfiedStepKeys, stepKey];
    // No key to merge: this path closes what the run opened itself, a frame later.
    endRun("Finished", null, false);
    // Only the tidy-up waits a frame, because the popup this press opened is not open yet: the tour
    // watches the click in the capture phase, so the control's own handler has not run. It works on
    // plain arrays, so it neither needs the run to still be active nor the overlay to still be up.
    cancelPendingEnd.current?.();
    cancelPendingEnd.current = scheduleFrame(() => {
      cancelPendingEnd.current = null;
      closeAnchorsOpenedByRun(steps, satisfied);
    });
  }, [endRun]);

  /**
   * The tour pressed an `OpenAnchor` step's control itself: the same record, without moving on.
   * One record for both kinds of press, so "already pressed", "still interactive" and "walk past
   * on Back" cannot come to three different answers about the same step.
   */
  const anchorOpened = useCallback(() => dispatch({ type: "anchorUsed" }), []);

  const getDueRun = useCallback(
    (tourKey: string) => {
      const tour = state.tours.find((candidate) => candidate.key === tourKey);
      return tour ? dueRun(tour, findProgress(state.progress, tourKey), renderContext) : null;
    },
    [state.tours, state.progress, renderContext]
  );

  const startTour = useCallback(
    (tourKey: string, options?: { replay?: boolean }) => {
      const tour = stateRef.current.tours.find((candidate) => candidate.key === tourKey);
      if (!tour) return;
      const run = options?.replay
        ? replayRun(tour, renderContext)
        : dueRun(tour, findProgress(stateRef.current.progress, tourKey), renderContext);
      if (run) dispatch({ type: "start", run });
    },
    [renderContext]
  );

  const getReplayRun = useCallback(
    (featureKey: string, keys?: TourKeyOverrides): TourRun | null => {
      const { tours, pages } = stateRef.current;
      // By key rather than by kind: a feature can carry more than one Page tour (schedule has its
      // catalog view), and "the first Page tour" would be whichever the API listed first.
      const resolved = resolveTourKeys(featureKey, keys);
      const byKey = (key: string | null) =>
        key === null ? undefined : tours.find((candidate) => candidate.key === key);
      const pageTour = byKey(resolved.page);
      const dataTour = pages[featureKey]?.hasData ? byKey(resolved.data) : undefined;
      return (
        (pageTour && replayRun(pageTour, renderContext)) ||
        (dataTour && replayRun(dataTour, renderContext)) ||
        null
      );
    },
    [renderContext]
  );

  const replayFeature = useCallback(
    (featureKey: string, keys?: TourKeyOverrides) => {
      const run = getReplayRun(featureKey, keys);
      if (run) dispatch({ type: "start", run });
    },
    [getReplayRun]
  );

  const requestReplay = useCallback(
    (featureKey: string, keys: TourKeyOverrides | undefined, path: string) => {
      // Always a new object, so choosing the same tour twice runs it twice.
      setPendingReplay({
        featureKey,
        pageKey: resolveTourKeys(featureKey, keys).page,
        path,
        standalone: keys?.dataTourKey === null,
      });
    },
    []
  );

  const clearReplay = useCallback((request: ReplayRequest) => {
    setPendingReplay((current) => (current === request ? null : current));
  }, []);

  const hasTours = useCallback(
    (featureKey: string, keys?: TourKeyOverrides) => {
      if (picking || state.status !== "ready") return false;
      // By key, the same tours `replayFeature` would run, so the Guided tours list never offers a
      // replay that does nothing (schedule's page tour while only the catalog tour can render).
      const resolved = resolveTourKeys(featureKey, keys);
      return state.tours.some(
        (tour) =>
          (tour.key === resolved.page || tour.key === resolved.data) &&
          renderableSteps(tour, renderContext).length > 0
      );
    },
    [picking, state.status, state.tours, renderContext]
  );

  const hasPageTour = useCallback(
    (featureKey: string, keys?: TourKeyOverrides) => {
      if (picking || state.status !== "ready") return false;
      const pageKey = resolveTourKeys(featureKey, keys).page;
      const tour = state.tours.find((candidate) => candidate.key === pageKey);
      return tour !== undefined && renderableSteps(tour, renderContext).length > 0;
    },
    [picking, state.status, state.tours, renderContext]
  );

  const startRun = useCallback((run: TourRun) => dispatch({ type: "start", run }), []);
  const queueRun = useCallback((run: TourRun) => dispatch({ type: "queue", run }), []);
  const updatePage = useCallback(
    (featureKey: string, registration: PageRegistration) =>
      dispatch({ type: "updatePage", featureKey, registration }),
    []
  );
  const unregisterPage = useCallback(
    (featureKey: string) => dispatch({ type: "unregisterPage", featureKey }),
    []
  );

  const value = useMemo<TourContextValue>(
    () => ({
      // "disabled" while picking, while a preview is loading, on screen or reporting a failure, and
      // for the rest of the visit to the page it ran on, is what holds auto-start back: every gate
      // in `useTour` reads it.
      status: picking || previewKey !== null || previewVisit ? "disabled" : state.status,
      whenRouteSettled,
      pathname: location.pathname,
      activeTour: state.active,
      hasTours,
      hasPageTour,
      getDueRun,
      updatePage,
      unregisterPage,
      startTour,
      startRun,
      queueRun,
      replayFeature,
      getReplayRun,
      pendingReplay,
      requestReplay,
      clearReplay,
      next,
      back,
      skip,
      finish,
    }),
    [
      picking,
      previewKey,
      previewVisit,
      state.status,
      state.active,
      whenRouteSettled,
      location.pathname,
      hasTours,
      hasPageTour,
      getDueRun,
      updatePage,
      unregisterPage,
      startTour,
      startRun,
      queueRun,
      replayFeature,
      getReplayRun,
      pendingReplay,
      requestReplay,
      clearReplay,
      next,
      back,
      skip,
      finish,
    ]
  );

  const active = state.active;
  // A data tour whose page lost its data points at rows that are gone: show centered cards.
  const suppressAnchor = active !== null && anchorSuppressed(state, active.run);

  return (
    <TourContext.Provider value={value}>
      {children}
      {previewError && (
        <TourPreviewNotice
          message={previewError}
          onDismiss={() => {
            setPreviewFailure(null);
            stripPreviewParam();
          }}
        />
      )}
      {pickKey && <TourPickMode tourKey={pickKey} />}
      {active && !picking && (
        <TourOverlay
          run={active.run}
          stepIndex={active.stepIndex}
          suppressAnchor={suppressAnchor}
          satisfiedStepKeys={active.satisfiedStepKeys}
          onNext={next}
          onBack={back}
          onSkip={skip}
          onAnchorUsed={anchorUsed}
          onAnchorOpened={anchorOpened}
        />
      )}
    </TourContext.Provider>
  );
};
